import is from '@sindresorhus/is';
import { useCallback, useRef } from 'react';

import type { CustomJobProfileTemplateRequest } from '../../../../../data-contract/job-profile-template';
import { useCustomTemplate } from './use-custom-template';
import { findManagedTemplateById, useManagedTemplates } from './use-managed-template';
import { useSaveTemplate } from './use-save-template';

type ActivateManagedTemplate = {
    readonly activate: (managedTemplateId: string) => Promise<boolean>;
    readonly isPending: boolean;
};

type InFlightActivation = {
    readonly managedTemplateId: string;
    readonly promise: Promise<boolean>;
};

export const useActivateManagedTemplate = (
    jobArchitectureId: string,
    enabled: boolean = true,
): ActivateManagedTemplate => {
    const managedTemplatesQuery = useManagedTemplates(enabled);
    const customTemplateQuery = useCustomTemplate(jobArchitectureId, enabled);
    const { mutateAsync, isPending } = useSaveTemplate();
    const inFlightRef = useRef<InFlightActivation | null>(null);

    const activate = useCallback(
        (managedTemplateId: string): Promise<boolean> => {
            const managedTemplate = findManagedTemplateById(managedTemplatesQuery.data ?? [], managedTemplateId);

            if (is.null(managedTemplate)) {
                return Promise.resolve(false);
            }

            const inFlight = inFlightRef.current;
            if (!is.null(inFlight) && inFlight.managedTemplateId === managedTemplate.id) {
                return inFlight.promise;
            }

            const customTemplate = customTemplateQuery.data ?? null;
            const templateId = customTemplate?.id ?? crypto.randomUUID();
            const payload: CustomJobProfileTemplateRequest = {
                cobrainerJobTemplateId: managedTemplate.id,
                description: managedTemplate.description,
                jobLevelSections: managedTemplate.jobLevelSections,
                jobRoleSections: managedTemplate.jobRoleSections,
                levelSkillLimits: managedTemplate.levelSkillLimits,
                name: managedTemplate.name,
                roleSkillLimits: managedTemplate.roleSkillLimits,
            };

            const promise = mutateAsync({ jobArchitectureId, payload, templateId })
                .then(() => true)
                .finally(() => {
                    if (inFlightRef.current?.promise === promise) {
                        inFlightRef.current = null;
                    }
                });

            inFlightRef.current = { managedTemplateId: managedTemplate.id, promise };

            return promise;
        },
        [jobArchitectureId, managedTemplatesQuery.data, customTemplateQuery.data, mutateAsync],
    );

    return { activate, isPending };
};
